/**
 * The one derivation for a plan question's stable id.
 *
 * Plan questions are stored as bare text (with optional alternatives); the id
 * an answer is keyed by has to be derived from that text on both sides of the
 * wire. The client derives it to key its staged answers
 * (`work/planQuestions.tsx`), and the server derives the same value to check
 * an answer against the gate's own questions (`workItems/gates.ts`). Two
 * hand-maintained copies of one hash can drift silently and drop every answer
 * on every send-back, so the derivation lives here and both sides import it.
 *
 * djb2 over the trimmed text, prefixed by the 1-based RAW array position (the
 * position the stored question list carried, not a post-filter index), base36.
 * Deterministic across processes and runs; not a security boundary.
 */
export function planQuestionId(text, index) {
    let hash = 5381;
    for (let i = 0; i < text.length; i += 1)
        hash = ((hash << 5) + hash + text.charCodeAt(i)) | 0;
    return `q${index + 1}-${(hash >>> 0).toString(36)}`;
}
const CHANGE_KEYS = [
    "addFiles",
    "addSteps",
    "editSteps",
    "addChecks",
    "addAcceptanceCriteria",
];
function changesNothing(change) {
    return CHANGE_KEYS.every((key) => (change?.[key]?.length ?? 0) === 0);
}
/** A step that only waits: "Wait for a person to read PR #2578 first". */
const WAIT_STEP = /\bwait(s|ing)?\b/i;
function stepText(step) {
    if (typeof step === "string")
        return step;
    if (!step || typeof step !== "object")
        return "";
    const { title, description } = step;
    return [title, description].filter((part) => typeof part === "string").join(" ");
}
/** An option whose only change is steps that wait for a person: it plans no different work. */
function onlyWaits(change) {
    const steps = change?.addSteps ?? [];
    const others = CHANGE_KEYS.filter((key) => key !== "addSteps");
    return (steps.length > 0 &&
        others.every((key) => (change?.[key]?.length ?? 0) === 0) &&
        steps.every((step) => WAIT_STEP.test(stepText(step))));
}
/**
 * A fact whose answer changes nothing in the plan (YAG-999): its
 * recommendation carries no plan change, and no alternative does either,
 * except one whose only change is to wait for a person. Whatever the answer,
 * the same work is planned, so asking it holds the plan for nothing.
 */
export function isPlanNeutral(question) {
    if (!changesNothing(question.recommendation?.planChange))
        return false;
    return (question.alternatives ?? []).every((option) => changesNothing(option?.planChange) || onlyWaits(option?.planChange));
}
/**
 * What a fact rests on that Reeve could not read: its `not_checked` lines,
 * joined; "" when it is marked `unreadSource` and names none; null when it
 * rests on nothing unread. The plan and the gate show it beside a stated
 * assumption ("source not read: ...").
 */
export function unreadSourceOf(question) {
    const lines = (Array.isArray(question.checked) ? question.checked : [])
        .filter((line) => line?.status === "not_checked" && typeof line.text === "string" && line.text.trim())
        .map((line) => line.text.trim());
    if (lines.length > 0)
        return lines.join("; ");
    return question.unreadSource === true ? "" : null;
}
/**
 * A stated assumption as one line: "Assumed: <answer> (source not read:
 * <source>)", the source part only when it rests on something Reeve could
 * not read. Null when the question is not a stated assumption.
 */
export function assumptionLine(question) {
    if (statedKind(question) !== "assumed")
        return null;
    const answer = question.recommendation?.answer;
    if (typeof answer !== "string")
        return null;
    const source = unreadSourceOf(question);
    const note = source === null ? "" : source ? ` (source not read: ${source})` : " (source not read)";
    return `Assumed: ${answer.trim()}${note}`;
}
function recommended(question) {
    const answer = question.recommendation?.answer;
    return typeof answer === "string" && answer.trim() !== "";
}
/**
 * A recommendation that widens the ticket (ENG-2966): its plan change adds
 * acceptance criteria, so taking it puts work in scope the ticket did not
 * ask for. A person decides that, never Reeve on his own.
 */
export function recommendationWidensScope(question) {
    return (question.recommendation?.planChange?.addAcceptanceCriteria?.length ?? 0) > 0;
}
/**
 * A decision Reeve states instead of asking: marked `decision` explicitly,
 * carrying a recommendation, and not marked one-way. It shows as "Reeve
 * decided" with a way to change it, and approving accepts it. A missing mark
 * is unknown, never a decision: an unmarked question stays a question for a
 * person, as do facts, questions with no recommendation and one-way choices,
 * and a decision whose recommendation widens the ticket
 * ({@link recommendationWidensScope}).
 */
export function isStatedDecision(question) {
    return (question.kind === "decision" &&
        recommended(question) &&
        question.oneWay !== true &&
        !recommendationWidensScope(question));
}
/**
 * Whether Reeve states this question on the plan, and how, or null when a
 * person answers it.
 *
 * - `decision`: {@link isStatedDecision}.
 * - `checked`: a fact Reeve read and answered: a recommendation and at least
 *   one line it checked.
 * - `assumed`: a fact Reeve could not settle, planned on: a recommendation
 *   whose plan change adds the step or check that tests it first, and no
 *   source it could not read. Or (YAG-999) a fact whose answer changes
 *   nothing in the plan (`isPlanNeutral`), even one resting on a source Reeve
 *   could not read: it shows that source, and a person can still answer it.
 *
 * A fact never stated: one a Team lacks the tool for (`needsTool`), and one
 * resting on a source Reeve could not read (`unreadSource`, or for an
 * assumption any line it could not check) when some answer would change the
 * plan. Those stay a person's, so a wrong guess about an unread screenshot
 * cannot ship silently.
 */
export function statedKind(question) {
    if (isStatedDecision(question))
        return "decision";
    if (question.kind !== "fact" || !recommended(question))
        return null;
    if (typeof question.needsTool === "string" && question.needsTool.trim())
        return null;
    const lines = Array.isArray(question.checked) ? question.checked : [];
    const unread = question.unreadSource === true;
    if (!unread && lines.some((line) => line?.status === "checked"))
        return "checked";
    if (isPlanNeutral(question))
        return "assumed";
    if (unread || lines.some((line) => line?.status === "not_checked"))
        return null;
    const change = question.recommendation?.planChange;
    const safeguard = (change?.addSteps?.length ?? 0) > 0 || (change?.addChecks?.length ?? 0) > 0;
    return safeguard ? "assumed" : null;
}
/** Does Reeve state this question (a decision, or a fact it checked or assumed)? */
export function isStatedQuestion(question) {
    return statedKind(question) !== null;
}
/**
 * Can Skip settle this question with Reeve's recommendation? Only when there
 * is one, and never on a fact (it is looked up, not chosen) or a one-way
 * choice (a person makes those).
 */
export function skipChooses(question) {
    return recommended(question) && question.kind !== "fact" && question.oneWay !== true;
}
/**
 * How many of the plan's questions are settled, as the Approve hold counts
 * them: a stated decision is settled without a click, every other question
 * once it carries an answer. The one count the page, the rail and the approve
 * route all read.
 */
export function planQuestionHold(questions) {
    return {
        answered: questions.filter((question) => question.answered || question.stated === true).length,
        total: questions.length,
    };
}
/** An option's stored `planChange`, its lists only; anything else reads as no change. */
function readPlanChange(option) {
    if (!option || typeof option !== "object" || Array.isArray(option))
        return null;
    const change = option.planChange;
    if (!change || typeof change !== "object" || Array.isArray(change))
        return null;
    const record = change;
    const out = {};
    for (const key of CHANGE_KEYS) {
        const value = record[key];
        if (Array.isArray(value))
            out[key] = value;
    }
    return out;
}
/**
 * The question list a plan gate carries, raw: its `planQuestions`, else, on a
 * one-attempt lane gate (one that names its attempt), the plan's own
 * `questions` the lane opens it with (`openPlanGateActivity`). An accepted
 * plan change writes `planQuestions` on a lane gate too, with its ids pinned,
 * and those win. Null when the gate carries neither (an older legacy gate, a
 * markdown-only plan), which every reader takes as nothing to hold.
 */
export function gatePlanQuestionEntries(payload) {
    if (!payload || typeof payload !== "object" || Array.isArray(payload))
        return null;
    const row = payload;
    if (Array.isArray(row.planQuestions))
        return row.planQuestions;
    const lane = typeof row.attemptId === "string" && row.attemptId.length > 0;
    return lane && Array.isArray(row.questions) ? row.questions : null;
}
/**
 * The gate's questions ({@link gatePlanQuestionEntries}), read for
 * settlement. Ids are derived exactly as every other reader derives them (an
 * explicit `id` wins, else the text at its raw position); an entry with no
 * text is dropped without shifting the positions around it. Total: any other
 * shape reads as no questions.
 */
export function readGateQuestionSettlement(payload) {
    const raw = gatePlanQuestionEntries(payload);
    if (!raw)
        return [];
    const out = [];
    raw.forEach((entry, index) => {
        let text = "";
        let explicitId = "";
        let marks = {};
        let recommendation = null;
        if (typeof entry === "string") {
            text = entry.trim();
        }
        else if (entry && typeof entry === "object" && !Array.isArray(entry)) {
            const row = entry;
            const rawText = typeof row.text === "string"
                ? row.text
                : typeof row.question === "string"
                    ? row.question
                    : "";
            text = rawText.trim();
            explicitId = typeof row.id === "string" ? row.id.trim() : "";
            const rec = row.recommendation;
            if (rec && typeof rec === "object" && !Array.isArray(rec)) {
                const answer = rec.answer;
                const why = rec.why;
                if (typeof answer === "string" && answer.trim()) {
                    recommendation = {
                        answer: answer.trim(),
                        why: typeof why === "string" ? why.trim() : "",
                    };
                }
            }
            marks = {
                kind: typeof row.kind === "string" ? row.kind : null,
                recommendation: recommendation
                    ? { ...recommendation, planChange: readPlanChange(rec) }
                    : null,
                alternatives: Array.isArray(row.alternatives)
                    ? row.alternatives.map((option) => ({ planChange: readPlanChange(option) }))
                    : null,
                oneWay: row.oneWay === true,
                needsTool: typeof row.needsTool === "string" ? row.needsTool : null,
                checked: Array.isArray(row.checked)
                    ? row.checked.filter((line) => Boolean(line) && typeof line === "object")
                    : null,
                unreadSource: row.unreadSource === true,
            };
        }
        if (!text)
            return;
        const kind = statedKind(marks);
        out.push({
            id: explicitId || planQuestionId(text, index),
            text,
            stated: kind !== null,
            statedKind: kind,
            skipChooses: skipChooses(marks),
            recommendation,
        });
    });
    return out;
}
//# sourceMappingURL=planQuestions.js.map